import * as React from 'react';
import { cn } from '@repo/utils';

type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  asChild?: boolean;
  children: React.ReactNode;
}

const variantClasses: Record<Variant, string> = {
  primary: 'bg-accent-600 text-white hover:brightness-90 active:brightness-75 active:scale-[0.98] disabled:bg-surface-2 disabled:text-text-muted',
  secondary: 'bg-transparent border border-border-strong text-text-primary hover:bg-surface-2 active:bg-surface-3 disabled:opacity-50',
  outline: 'bg-transparent border border-border text-text-primary hover:bg-surface-2 active:bg-surface-3 disabled:opacity-50',
  ghost: 'bg-transparent text-text-secondary hover:bg-surface-2 active:bg-surface-3 disabled:opacity-50',
  danger: 'bg-danger-500 text-white hover:opacity-90 active:scale-[0.98] disabled:opacity-50',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-11 px-6 text-base',
};

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  asChild = false,
  disabled,
  className,
  children,
  ...props
}: ButtonProps) {
  const classes = cn(
    'inline-flex items-center justify-center gap-2 font-medium rounded-sm whitespace-nowrap transition-all duration-instant ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg cursor-pointer select-none',
    variantClasses[variant],
    sizeClasses[size],
    className,
  );

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<{ className?: string }>, {
      className: cn(classes, (children.props as { className?: string })?.className),
    });
  }

  return (
    <button
      {...props}
      disabled={disabled || loading}
      aria-busy={loading}
      className={classes}
    >
      {loading ? (
        <>
          <svg
            className="kraphene-spinner"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="8 20" />
          </svg>
          <span>{children}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}
